import { inject, NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { MyOrganizationsMobileComponent } from '@clarilog/modules2/organizations/modules/my-organizations-mobile/my-organizations-mobile.component';
import { AppComponent } from './app.component';
import { AuthCoreGuard, AuthThemeLoaderGuard } from './core';
import { CoreDefaultChildRouteRedirection } from './core/services2';
import { CoreContentLayoutComponent } from './layouts2/content-layout/content-layout.component';
import { MobileLayoutComponent } from './layouts2/mobile-layout/mobile-layout.component';
import { ErrorComponent } from './shared2/pages/error/error.component';
import { NoLicenseComponent } from './shared2/pages/no-license/no-license.component';
import { NotFoundComponent } from './shared2/pages/not-found/not-found.component';
import { UnauthorizedComponent } from './shared2/pages/unauthorized/unauthorized.component';
import { SoftwareModule } from '@clarilog/modules2/software/software.module';
import { ContractsModule } from '@clarilog/modules2/contracts/contracts.module';
import { AssetsModule } from '@clarilog/modules2/assets/assets.module';
import { FinancialsModule } from '@clarilog/modules2/financials/financials.module';
import { InventoryModule } from '@clarilog/modules2/inventory/inventory.module';
import { TeleDistributionModule } from '@clarilog/modules2/teledistribution/teledistribution.module';
import { StocksModule } from '@clarilog/modules2/stocks/stocks.module';
import { LoansModule } from '@clarilog/modules2/loans/loans.module';
import { UsersModule } from '@clarilog/modules2/users/users.module';
import { SuppliersModule } from '@clarilog/modules2/suppliers/suppliers.module';
import { TaskModule } from '@clarilog/modules2/task/task.module';
import { TicketModule } from '@clarilog/modules2/ticket/ticket.module';
import { FavoriteModule } from '@clarilog/modules2/ticket/modules/favorite/favorite.module';
import { IncidentModule } from '@clarilog/modules2/incident/incident.module';
import { RequestModule } from '@clarilog/modules2/request/request.module';
import { ProblemModule } from '@clarilog/modules2/problem/problem.module';
import { ChangeModule } from '@clarilog/modules2/change/change.module';
import { ArticleKnowledgeModule } from '@clarilog/modules2/article-knowledge/article-knowledge.module';
import { AdministrationModule } from '@clarilog/modules2/administration/administration.module';
import { AlertsModule } from '@clarilog/modules2/alerts/alerts.module';
import { QueryBuildersModule } from '@clarilog/modules2/query-builders/query-builders.module';

/** Gestion des routes. */
const routes: Routes = [
  {
    path: '',
    component: AppComponent,
    canActivate: [AuthCoreGuard],
    canActivateChild: [AuthThemeLoaderGuard, AuthCoreGuard],
    children: [
      {
        path: '',
        component: CoreContentLayoutComponent,
        canActivate: [AuthCoreGuard],
        children: [
          // {
          //   path: '',
          //   redirectTo: () => {
          //     const routeRedirection = inject(CoreDefaultChildRouteRedirection);
          //     return routeRedirection.getRedirectToPath(
          //       'dashboard',
          //       'dashboard',
          //     );
          //   },
          //   pathMatch: 'full',
          // },
          {
            path: 'dashboard',
            loadChildren: () =>
              import('./modules2/dashboard/dashboard.module').then(
                (m) => m.DashboardModule,
              ),
          },
          {
            path: 'reports',
            loadChildren: () =>
              import('@clarilog/modules2/report/report.module').then(
                (m) => m.ReportModule,
              ),
          },
          {
            path: 'contracts',
            data: {
              theme: ContractsModule.currentTheme,
            },
            loadChildren: () =>
              import('@clarilog/modules2/contracts/contracts.module').then(
                (m) => m.ContractsModule,
              ),
          },
          {
            path: 'assets',
            data: {
              theme: AssetsModule.currentTheme,
            },
            loadChildren: () =>
              import('@clarilog/modules2/assets/assets.module').then(
                (m) => m.AssetsModule,
              ),
          },
          {
            path: 'financials',
            data: {
              theme: FinancialsModule.currentTheme,
            },
            loadChildren: () =>
              import('@clarilog/modules2/financials/financials.module').then(
                (m) => m.FinancialsModule,
              ),
          },
          {
            path: 'inventory',
            data: {
              theme: InventoryModule.currentTheme,
            },
            loadChildren: () =>
              import('@clarilog/modules2/inventory/inventory.module').then(
                (m) => m.InventoryModule,
              ),
          },
          {
            path: 'teledistribution',
            data: {
              theme: TeleDistributionModule.currentTheme,
            },
            loadChildren: () =>
              import(
                '@clarilog/modules2/teledistribution/teledistribution.module'
              ).then((m) => m.TeleDistributionModule),
          },
          {
            path: 'stocks',
            data: {
              theme: StocksModule.currentTheme,
            },
            loadChildren: () =>
              import('@clarilog/modules2/stocks/stocks.module').then(
                (m) => m.StocksModule,
              ),
          },
          {
            path: 'loans',
            data: {
              theme: LoansModule.currentTheme,
            },
            loadChildren: () =>
              import('@clarilog/modules2/loans/loans.module').then(
                (m) => m.LoansModule,
              ),
          },
          {
            path: 'users',
            data: {
              theme: UsersModule.currentTheme,
            },
            loadChildren: () =>
              import('@clarilog/modules2/users/users.module').then(
                (m) => m.UsersModule,
              ),
          },
          {
            path: 'suppliers',
            data: {
              theme: SuppliersModule.currentTheme,
            },
            loadChildren: () =>
              import('@clarilog/modules2/suppliers/suppliers.module').then(
                (m) => m.SuppliersModule,
              ),
          },
          {
            path: 'tasks',
            data: {
              theme: TaskModule.currentTheme,
            },
            loadChildren: () =>
              import('@clarilog/modules2/task/task.module').then(
                (m) => m.TaskModule,
              ),
          },
          {
            path: 'tickets',
            data: {
              theme: TicketModule.currentTheme,
            },
            loadChildren: () =>
              import('@clarilog/modules2/ticket/ticket.module').then(
                (m) => m.TicketModule,
              ),
          },
          {
            path: 'favorite-tickets',
            data: {
              theme: FavoriteModule.currentTheme,
            },
            loadChildren: () =>
              import(
                '@clarilog/modules2/ticket/modules/favorite/favorite.module'
              ).then((m) => m.FavoriteModule),
          },
          {
            path: 'incidents',
            data: {
              theme: IncidentModule.currentTheme,
            },
            loadChildren: () =>
              import('@clarilog/modules2/incident/incident.module').then(
                (m) => m.IncidentModule,
              ),
          },
          {
            path: 'requests',
            data: {
              theme: RequestModule.currentTheme,
            },
            loadChildren: () =>
              import('@clarilog/modules2/request/request.module').then(
                (m) => m.RequestModule,
              ),
          },
          {
            path: 'problems',
            data: {
              theme: ProblemModule.currentTheme,
            },
            loadChildren: () =>
              import('@clarilog/modules2/problem/problem.module').then(
                (m) => m.ProblemModule,
              ),
          },
          {
            path: 'changes',
            data: {
              theme: ChangeModule.currentTheme,
            },
            loadChildren: () =>
              import('@clarilog/modules2/change/change.module').then(
                (m) => m.ChangeModule,
              ),
          },
          {
            path: 'article-knowledge',
            data: {
              theme: ArticleKnowledgeModule.currentTheme,
            },
            loadChildren: () =>
              import(
                '@clarilog/modules2/article-knowledge/article-knowledge.module'
              ).then((m) => m.ArticleKnowledgeModule),
          },
          {
            path: 'administration',
            data: {
              theme: AdministrationModule.currentTheme,
            },
            loadChildren: () =>
              import(
                '@clarilog/modules2/administration/administration.module'
              ).then((m) => m.AdministrationModule),
          },
          {
            path: 'alerts',
            data: {
              theme: AlertsModule.currentTheme,
            },
            loadChildren: () =>
              import('@clarilog/modules2/alerts/alerts.module').then(
                (m) => m.AlertsModule,
              ),
          },
          {
            path: 'notifications',

            loadChildren: () =>
              import(
                '@clarilog/modules2/notification/notification.module'
              ).then((m) => m.NotificationModule),
          },
          {
            path: 'softwares',
            data: {
              theme: SoftwareModule.currentTheme,
            },
            loadChildren: () =>
              import('@clarilog/modules2/software/software.module').then(
                (m) => m.SoftwareModule,
              ),
          },
          {
            path: 'home',
            loadChildren: () =>
              import(
                '@clarilog/modules2/dashboard-user/dashboard-user.module'
              ).then((m) => m.DashboardUserModule),
          },
          {
            path: 'query-builders',
            data: {
              theme: QueryBuildersModule.currentTheme,
            },
            loadChildren: () =>
              import(
                '@clarilog/modules2/query-builders/query-builders.module'
              ).then((m) => m.QueryBuildersModule),
          },
          {
            path: 'help-desk-incident',
            loadChildren: () =>
              import(
                '@clarilog/modules2/help-desk-incident/help-desk-incident.module'
              ).then((m) => m.HelpDeskUserIncidentModule),
          },
          {
            path: 'help-desk-request',
            loadChildren: () =>
              import(
                '@clarilog/modules2/help-desk-request/help-desk-request.module'
              ).then((m) => m.HelpDeskUserRequestModule),
          },
          {
            path: 'help-desk-change',
            loadChildren: () =>
              import(
                '@clarilog/modules2/help-desk-change/help-desk-change.module'
              ).then((m) => m.HelpDeskUserChangeModule),
          },
          {
            path: 'help-desk-knowledge',
            loadChildren: () =>
              import(
                '@clarilog/modules2/help-desk-knowledge/help-desk-knowledge.module'
              ).then((m) => m.HelpDeskUserKnowledgeModule),
          },
          {
            path: 'help-desk-my-asset',
            loadChildren: () =>
              import(
                '@clarilog/modules2/help-desk-myAsset/help-desk-my-asset.module'
              ).then((m) => m.HelpDeskUserMyAssetModule),
          },
          {
            path: 'help-desk-my-loan',
            loadChildren: () =>
              import(
                '@clarilog/modules2/help-desk-myLoan/help-desk-my-loan.module'
              ).then((m) => m.HelpDeskUserMyLoanModule),
          },
          {
            path: 'help-desk-my-ticket',
            loadChildren: () =>
              import(
                '@clarilog/modules2/help-desk-myTicket/help-desk-my-ticket.module'
              ).then((m) => m.HelpDeskUserMyTicketModule),
          },
        ],
      },
      {
        path: 'mobile',
        component: MobileLayoutComponent,
        canActivate: [AuthCoreGuard],
        children: [
          {
            path: 'user',
            loadChildren: () =>
              import(
                '@clarilog/modules2/mobile-help-desk/mobile-help-desk.module'
              ).then((m) => m.MobileHelpDeskModule),
          },
          {
            path: '',
            loadChildren: () =>
              import('@clarilog/modules2/mobile/mobile.module').then(
                (m) => m.MobileModule,
              ),
          },
        ],
      },
      {
        path: 'organizations',
        loadChildren: () =>
          import('@clarilog/modules2/organizations/organizations.module').then(
            (m) => m.OrganizationsModule,
          ),
      },
      {
        path: 'file',
        loadChildren: () =>
          import('@clarilog/modules2/file/file.module').then(
            (m) => m.FileModule,
          ),
      },
      {
        path: 'asset-affected',
        loadChildren: () =>
          import(
            '@clarilog/modules2/asset-affected/asset-affected.module'
          ).then((m) => m.AssetAffectedModule),
      },
      {
        path: 'organizations/my-organizations/mobile',
        component: MyOrganizationsMobileComponent,
      },
    ],
  },
  {
    path: 'error',
    component: ErrorComponent,
  },
  {
    path: 'unauthorized',
    component: UnauthorizedComponent,
  },
  {
    path: 'no-license',
    component: NoLicenseComponent,
  },
  {
    path: '**',
    component: NotFoundComponent,
  },
];
/**
 * Import des modules et des fournisseurs
 * les providers 'AuthCoreGuardService' et 'AuthService' servent à l'authentification
 */
@NgModule({
  imports: [
    RouterModule.forRoot(routes, { paramsInheritanceStrategy: 'emptyOnly' }),
  ],
  exports: [RouterModule],
  providers: [AuthCoreGuard],
})
export class AppRoutingModule {
  constructor() {}
}
